/**
 * TemplateEditor — sl-e0il.1.2
 *
 * Plain-text CodeMirror-6 editor used for AI system/user prompts and
 * HTTP free-form body fields. No language highlighting — template token
 * decoration and autocomplete are added via the `extensions` prop (see
 * `template-decoration.ts` / `completion-source.ts`, wired in
 * sl-e0il.4.1).
 *
 * Exposes `EditorAdapter` via `forwardRef`; shares the same adapter
 * helper as `<CodeEditor>` (DRY via `createCodeMirrorAdapter`).
 */

import type { Extension } from '@codemirror/state';
import { tooltips } from '@codemirror/view';
import CodeMirror from '@uiw/react-codemirror';
import { forwardRef, useImperativeHandle, useMemo, useRef } from 'react';

import * as styles from './editor.css';
import type { EditorAdapter } from './editor-adapter';
import {
  createCodeMirrorAdapter,
  useEchoSuppressedValue,
} from './internal/codemirror-helpers';

export interface TemplateEditorProps {
  value: string;
  onChange: (value: string) => void;
  readOnly?: boolean;
  placeholder?: string;
  /** Extra extensions — typically template decoration + completion. */
  extensions?: readonly Extension[];
  height?: string;
  /** See `CodeEditorProps.fill`. */
  fill?: boolean;
  /** See `CodeEditorProps.tooltipsParent`. */
  tooltipsParent?: HTMLElement | null;
}

export const TemplateEditor = forwardRef<EditorAdapter, TemplateEditorProps>(
  function TemplateEditor(props, ref) {
    const viewRef = useRef<import('@codemirror/view').EditorView | null>(null);

    useImperativeHandle(
      ref,
      () =>
        createCodeMirrorAdapter({
          get current() {
            return viewRef.current;
          },
        }),
      []
    );

    // Race guard: panel commits onChange asynchronously through Y.js,
    // so a fast typist races against the echo and CodeMirror would
    // otherwise reset its own doc to a stale value mid-edit. See the
    // hook comment in `internal/codemirror-helpers.ts` for details.
    const { value: guardedValue, onChange: guardedOnChange } =
      useEchoSuppressedValue(props.value, props.onChange);

    // Tooltip portal strategy: default `document.body` (escapes the
    // bottom dock's `backdrop-filter` containing block and its
    // `overflow: hidden`). Body is a block element so the
    // `position: relative` Ghost container CodeMirror appends has no
    // layout impact. Fullscreen dialog overrides with an absolute-
    // positioned host inside the Modal tree — parenting to the
    // Modal's flex-column `Dialog.Content` directly would turn the
    // Ghost into a flex-item and break the editor's height. The Radix
    // Dialog aria-hidden / pointer-events case is handled via
    // `pointer-events: auto` on `.cm-tooltip` in `editor.css.ts`.
    const tooltipsParent = props.tooltipsParent;
    const extensions = useMemo(
      () => [
        tooltips({ parent: tooltipsParent ?? document.body }),
        ...(props.extensions ?? []),
      ],
      [tooltipsParent, props.extensions]
    );

    const rootClassName = [
      styles.editorRoot,
      props.fill ? styles.editorRootFill : '',
      props.readOnly ? styles.editorReadOnly : '',
    ]
      .filter(Boolean)
      .join(' ');

    return (
      <div className={rootClassName}>
        <CodeMirror
          value={guardedValue}
          onChange={guardedOnChange}
          extensions={extensions}
          readOnly={props.readOnly}
          placeholder={props.placeholder}
          height={props.fill ? '100%' : (props.height ?? 'auto')}
          basicSetup={{
            lineNumbers: true,
            foldGutter: false,
            highlightActiveLine: !props.readOnly,
            highlightActiveLineGutter: false,
            bracketMatching: false,
            closeBrackets: false,
            autocompletion: false,
            indentOnInput: false,
          }}
          onCreateEditor={view => {
            viewRef.current = view;
          }}
        />
      </div>
    );
  }
);
